import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import Link from '@mui/material/Link';
import { Box, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { AboutYageoGroupOverviewOverviewBrandsContent } from '../../graphql/pimcore/modules/siteContent/types/AboutYageoGroupOverviewContent';

interface AccordionItem {
    label: string;
    content: string[];
    imgSrc: string;
    link: string | null;
}

interface OurBrandsExpandListProps {
    items: AccordionItem[];
    cmsContent: AboutYageoGroupOverviewOverviewBrandsContent;
}

const OurBrandsExpandList: FunctionComponent<OurBrandsExpandListProps> = ({
    items,
    cmsContent,
}) => {
    const [expanded, setExpanded] = useState<number[]>([]);
    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();

    const handleAccordionChange =
        (panel: number) => (e: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded((prev) =>
                isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
            );
        };

    return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 700,
                        fontSize: '30px',
                        color: '#B75332',
                    }}
                >
                    {cmsContent.title.content}
                </Typography>
            </Box>
            <Box sx={{ bgcolor: '#E5673E12', padding: '1.5rem' }}>
                {items.map((item, index) => {
                    const imageUrl = item.imgSrc;
                    const href = item.link;

                    const firstPara = item.content[0] || '';
                    const lower = firstPara.toLowerCase();
                    const lbl = item.label.toLowerCase();
                    const pos = lower.indexOf(lbl);
                    const before = pos > -1 ? firstPara.slice(0, pos) : firstPara;
                    const match =
                        pos > -1 ? firstPara.slice(pos, pos + item.label.length) : item.label;
                    const after = pos > -1 ? firstPara.slice(pos + item.label.length) : '';

                    return (
                        <Accordion
                            key={index}
                            expanded={expanded.includes(index)}
                            onChange={handleAccordionChange(index)}
                            sx={{ marginBottom: '1rem', backgroundColor: '#FAE1D8' }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: expanded.includes(index) ? '#B75332' : 'white',
                                    color: expanded.includes(index) ? 'white' : '#E56731',
                                    '& .MuiSvgIcon-root': {
                                        color: expanded.includes(index) ? 'white' : '#E56731',
                                    },
                                }}
                            >
                                <Typography
                                    sx={{ fontSize: isScreenXs ? '16px' : '20px', fontWeight: 500 }}
                                >
                                    {item.label}
                                </Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Box
                                    sx={{
                                        bgcolor: 'white',
                                        padding: '0.5rem',
                                        marginTop: '0.5rem',
                                    }}
                                >
                                    {imageUrl && (isScreenMd || isScreenSm || isScreenXs) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            {href ? (
                                                <Link
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="none"
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt={item.label}
                                                        style={{ width: 202, height: 'auto' }}
                                                    />
                                                </Link>
                                            ) : (
                                                <img
                                                    src={imageUrl}
                                                    alt={item.label}
                                                    style={{ width: 202, height: 'auto' }}
                                                />
                                            )}
                                        </Box>
                                    )}

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection:
                                                isScreenMd || isScreenSm || isScreenXs
                                                    ? 'column'
                                                    : 'row',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginTop: '0.3rem',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                flex: 1,
                                                paddingRight: isScreenXs ? 0 : '1rem',
                                                paddingTop: '0.5rem',
                                            }}
                                        >
                                            <Typography
                                                paragraph
                                                sx={{
                                                    textAlign:
                                                        isScreenSm || isScreenXs
                                                            ? 'left'
                                                            : 'justify',
                                                }}
                                            >
                                                {before}
                                                {href ? (
                                                    <Link
                                                        href={href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ color: 'black', fontWeight: 700 }}
                                                    >
                                                        {match}
                                                    </Link>
                                                ) : (
                                                    <strong>{match}</strong>
                                                )}
                                                {after}
                                            </Typography>
                                        </Box>

                                        {imageUrl && !(isScreenMd || isScreenSm || isScreenXs) && (
                                            <Box sx={{ flexShrink: 0 }}>
                                                {href ? (
                                                    <Link
                                                        href={href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="none"
                                                    >
                                                        <img
                                                            src={imageUrl}
                                                            alt={item.label}
                                                            style={{
                                                                width: 202,
                                                                height: 'auto',
                                                                marginBottom: '0.5rem',
                                                            }}
                                                        />
                                                    </Link>
                                                ) : (
                                                    <img
                                                        src={imageUrl}
                                                        alt={item.label}
                                                        style={{
                                                            width: 202,
                                                            height: 'auto',
                                                            marginBottom: '0.5rem',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    <Box>
                                        {item.content.slice(1).map((paragraph, idx) => {
                                            const parser = new DOMParser();
                                            const doc = parser.parseFromString(
                                                paragraph,
                                                'text/html'
                                            );
                                            const link = doc.querySelector('a');

                                            return (
                                                <Typography
                                                    key={idx}
                                                    paragraph
                                                    sx={{
                                                        textAlign:
                                                            isScreenSm || isScreenXs
                                                                ? 'left'
                                                                : 'justify',
                                                    }}
                                                >
                                                    {link ? (
                                                        <>
                                                            {paragraph.substring(
                                                                0,
                                                                paragraph.indexOf(link.outerHTML)
                                                            )}
                                                            <Link
                                                                component="a"
                                                                href={
                                                                    link.getAttribute('href') || '#'
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{
                                                                    color: '#B75332',
                                                                    textDecoration: 'none',
                                                                    '&:hover': {
                                                                        textDecoration: 'underline',
                                                                    },
                                                                }}
                                                            >
                                                                {link.textContent}
                                                            </Link>
                                                            {paragraph.substring(
                                                                paragraph.indexOf(link.outerHTML) +
                                                                    link.outerHTML.length
                                                            )}
                                                        </>
                                                    ) : (
                                                        paragraph
                                                    )}
                                                </Typography>
                                            );
                                        })}{' '}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        </Box>
    );
};

export default OurBrandsExpandList;
